<template>
  <div class="debt-installment-bill-filter">
    <!-- First Row: Store, Date Range, Installment Service -->
    <b-row class="debt-installment-bill-filter__row mb-5">
      <b-col>
        <b-form>
          <Autosuggest
            :model="searchStore"
            :suggestions="filteredStoreOptions"
            placeholder="cửa hàng"
            :limit="10"
            @select="onStoreSelected"
            @change="onStoreInputChange"
            :disabled="isStoreDisabled"
            class="debt-installment-bill-filter__store-input"
            :aria-label="storeInputAriaLabel"
          />
        </b-form>
      </b-col>

      <b-col>
        <div class="debt-installment-bill-filter__date-range">
          <b-input-group>
            <date-picker
              placeholder="Từ"
              class="form-control form-control-sm debt-installment-bill-filter__date-input"
              :config="datePickerConfig"
              v-model="localFilters.fromDate"
              :aria-label="fromDateAriaLabel"
            />
          </b-input-group>
          <span class="debt-installment-bill-filter__date-separator ml-1 mr-1"></span>
          <b-input-group>
            <date-picker
              placeholder="Đến"
              class="form-control form-control-sm debt-installment-bill-filter__date-input"
              :config="datePickerConfig"
              v-model="localFilters.toDate"
              :aria-label="toDateAriaLabel"
            />
          </b-input-group>
        </div>
      </b-col>

      <b-col>
        <b-form>
          <b-form-select
            v-model="localFilters.contactId"
            :options="installmentOptions"
            size="sm"
            class="debt-installment-bill-filter__select"
            :aria-label="installmentSelectAriaLabel"
          />
        </b-form>
      </b-col>

      <b-col>
        <b-input
          placeholder="Mã HĐ"
          v-model.trim="localFilters.contractCode"
          size="sm"
          class="debt-installment-bill-filter__input"
          :aria-label="contractCodeAriaLabel"
        />
      </b-col>

      <b-col>
        <b-input
          placeholder="Chứng từ"
          v-model.trim="localFilters.docId"
          size="sm"
          class="debt-installment-bill-filter__input"
          :aria-label="docIdAriaLabel"
        />
      </b-col>
    </b-row>

    <!-- Second Row: Customer Mobile, Payment Status, Filter Button -->
    <b-row class="debt-installment-bill-filter__row mb-5">
      <b-col>
        <b-form>
          <b-input
            placeholder="Tìm chính xác SĐT KH"
            v-model.trim="localFilters.customerMobile"
            size="sm"
            class="debt-installment-bill-filter__input"
            :aria-label="customerMobileAriaLabel"
          />
        </b-form>
      </b-col>

      <b-col>
        <b-form>
          <b-input
            placeholder="Tìm tên khách hàng"
            v-model.trim="localFilters.customerName"
            size="sm"
            class="debt-installment-bill-filter__input"
            :aria-label="customerNameAriaLabel"
          />
        </b-form>
      </b-col>

      <b-col>
        <b-form>
          <b-form-select
            v-model="localFilters.isFullPaid"
            :options="paidStatusOptions"
            size="sm"
            class="debt-installment-bill-filter__select"
            :aria-label="paidStatusAriaLabel"
          />
        </b-form>
      </b-col>

      <b-col>
        <b-button
          variant="primary"
          size="sm"
          @click="applyFilters"
          :disabled="isLoading"
          class="debt-installment-bill-filter__apply-btn"
          :aria-label="applyButtonAriaLabel"
        >
          <b-spinner small v-if="isLoading"></b-spinner>
          <span v-else>Lọc</span>
        </b-button>
      </b-col>


      <b-col></b-col>
    </b-row>
  </div>
</template>

<script>
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';

export default {
  name: 'DebtInstallmentBillFilter',
  components: {
    Autosuggest,
  },
  props: {
    /**
     * Filter values object
     */
    value: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Available installment options
     */
    installmentOptions: {
      type: Array,
      default: () => [],
    },
    /**
     * Available store options
     */
    storeOptions: {
      type: Array,
      default: () => [],
    },
    /**
     * Loading state
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localFilters: {
        fromDate: null,
        toDate: null,
        storeId: null,
        contactId: null,
        contractCode: null,
        docId: null,
        customerMobile: null,
        customerName: null,
        isFullPaid: null,
      },
      searchStore: '',
      filteredStoreOptions: [],
      isStoreDisabled: false,

      // Date picker configuration
      datePickerConfig: {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        showClear: true,
        showClose: true,
        minDate: false,
        maxDate: false,
        locale: 'vi',
        sideBySide: true,
      },

      // Payment status options
      paidStatusOptions: [
        { value: null, text: 'Công nợ trả góp' },
        { value: 'true', text: 'Hết nợ' },
        { value: 'false', text: 'Còn nợ' },
      ],
    };
  },
  computed: {
    // Accessibility labels
    storeInputAriaLabel() {
      return 'Chọn cửa hàng để lọc dữ liệu';
    },
    fromDateAriaLabel() {
      return 'Chọn ngày bắt đầu để lọc';
    },
    toDateAriaLabel() {
      return 'Chọn ngày kết thúc để lọc';
    },
    installmentSelectAriaLabel() {
      return 'Chọn dịch vụ trả góp để lọc';
    },
    contractCodeAriaLabel() {
      return 'Nhập mã hợp đồng để tìm kiếm';
    },
    docIdAriaLabel() {
      return 'Nhập số chứng từ để tìm kiếm';
    },
    customerMobileAriaLabel() {
      return 'Nhập số điện thoại khách hàng để tìm kiếm';
    },
    customerNameAriaLabel() {
      return 'Nhập tên khách hàng để tìm kiếm';
    },
    paidStatusAriaLabel() {
      return 'Chọn trạng thái thanh toán để lọc';
    },
    applyButtonAriaLabel() {
      return 'Áp dụng bộ lọc để tìm kiếm dữ liệu';
    },
  },
  watch: {
    value: {
      handler(newValue) {
        this.localFilters = { ...this.localFilters, ...newValue };
      },
      immediate: true,
      deep: true,
    },
    storeOptions: {
      handler(newOptions) {
        this.filteredStoreOptions = [...newOptions];
        this.initializeStoreSelection();
      },
      immediate: true,
    },
  },
  mounted() {
    // Add keyboard event listener for Enter key
    window.addEventListener('keyup', this.onFilterKeyEnter);
  },
  beforeDestroy() {
    // Remove keyboard event listener
    window.removeEventListener('keyup', this.onFilterKeyEnter);
  },
  methods: {
    /**
     * Apply filters and emit to parent
     */
    applyFilters() {
      this.$emit('input', { ...this.localFilters });
      this.$emit('apply-filters', { ...this.localFilters });
    },

    /**
     * Handle Enter key press for filtering
     */
    onFilterKeyEnter(event) {
      if (event.key === 'Enter') {
        this.applyFilters();
      }
    },

    /**
     * Handle store selection from autocomplete
     */
    onStoreSelected(option) {
      this.searchStore = option.item.name;
      this.localFilters.storeId = option.item.id;
    },

    /**
     * Handle store input change for autocomplete
     */
    onStoreInputChange(text) {
      if (!text) {
        text = '';
      }
      this.searchStore = text;

      const filteredData = this.storeOptions
        .filter((item) => {
          return (
            item.name.toLowerCase().indexOf(text.toLowerCase()) > -1 ||
            (item.shortName &&
              item.shortName.toLowerCase().indexOf(text.toLowerCase()) > -1)
          );
        })
        .slice(0, 10);

      this.filteredStoreOptions = [...filteredData];
    },

    /**
     * Initialize store selection based on available options
     */
    initializeStoreSelection() {
      if (this.storeOptions.length === 1) {
        // Only one store available, auto-select it
        const store = this.storeOptions[0];
        this.searchStore = store.name;
        this.localFilters.storeId = store.id;
        this.isStoreDisabled = true;
      } else if (this.storeOptions.length > 1) {
        // Multiple stores, set default to "All stores"
        const defaultStore = this.storeOptions.find(
          (store) => store.id === null,
        );
        if (defaultStore) {
          this.searchStore = defaultStore.name;
          this.localFilters.storeId = defaultStore.id;
        }
        this.isStoreDisabled = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.debt-installment-bill-filter {
  &__date-range {
    display: flex;
    align-items: center;
  }

  &__date-input,
  &__input,
  &__select {
    border: 1px solid #ced4da;
    box-shadow: none !important;
  }

  &__apply-btn {
    font-weight: 600;
    width: 70px;
  }
}
</style>
